import React, { useState, useEffect } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import {
  TextField,
  Select,
  MenuItem,
  Button,
  Grid,
  FormControl,
  Typography,
  Autocomplete,
  IconButton,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Box,
  Pagination,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  DialogContentText
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createLeadAction, updateLead, getLeadsByIdAction, getDeleteNoteAction } from '../../redux/actions/leadActions';
import { searchUserOnTaskAction } from '../../redux/actions/taskActions';
import { useNavigate, useLocation } from 'react-router-dom';
import DatePickerField from '../../common/DatePickerDashboard';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { convertToE164 } from '../../common/helpers/commonFun';
import { CustomTableCell } from '../../common/CustomTableCell';
import { formatPhoneNumber } from '../../common/helpers/commonFun';
import PhoneNumberField from '../../common/PhoneNumberField';

// put this near the top of the component file (inside the component is fine)
const INITIAL_FORM = {
  firstName: '',
  middleName: '',
  lastName: '',
  phoneNumber: '',
  rawPhoneNumber: '',
  email: '',
  quoteNumber: '',
  status: '',
  assignedTo: '',
  assignedToName: '',
  type: '',
  lineOfBusiness: '',
  leadSource: '',
  notes: [],
  followUpDate: null,
};


const CreateLeadForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const dispatch = useDispatch();
  const { state } = useLocation();
  const leadData = state?.leadData;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const isEditMode = !!leadData;
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_FORM);

  const [errors, setErrors] = useState({});
  const [userSearchTimeout, setUserSearchTimeout] = useState(null);

  const searchUsers = useSelector((state) => state.task.searchUser?.content || []);
  const [editNoteDialog, setEditNoteDialog] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [editableNote, setEditableNote] = useState({ type: '', message: '' });
  const [touchedFields, setTouchedFields] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingNav, setPendingNav] = useState(null); // { path, state, proceed }
  const [saving, setSaving] = useState(false);
 const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
const [noteDialogErrors, setNoteDialogErrors] = useState({ type: '', message: '' });

const hardReset = React.useCallback(() => {
  setFormData(INITIAL_FORM);
  setErrors({});
  setTouchedFields({});
  setPage(0);
  setRowsPerPage(5);
  dispatch({ type: 'RESET_ALL_LEADS' });
}, [dispatch]);

const buildCreatePayload = () => ({
  firstName: formData.firstName,
  middleName: formData.middleName,
  lastName: formData.lastName,
  phoneNumber: convertToE164(formData.rawPhoneNumber),
  email: formData.email,
  type: formData.type,
  leadSource: formData.leadSource,
  status: formData.status,
  quoteNumber: formData.quoteNumber,
  lineOfBusiness: formData.lineOfBusiness,
  notes: formData.notes ?? [],
  assignedTo: formData.assignedTo,
  followUpDate: formData.followUpDate,
   ...(isEditMode && { id: leadData.id }),
});

const saveAndResetThenProceed = async () => {
  if (!pendingNav) return;
  try {
    setSaving(true);
    // If we're editing an existing lead, you can call updateLead here instead.
    // Your requirement mentions "Create Lead page", so we assume create:
    const payload = buildCreatePayload();

    await new Promise((resolve, reject) => {
      if (isEditMode) {
        dispatch(
          updateLead(
            payload,
            () => resolve(),
            (err) => reject(err || 'Failed to update lead')
          )
        );
      } else {
        dispatch(
          createLeadAction(
            payload,
            () => resolve(),
            (err) => reject(err || 'Failed to save lead')
          )
        );
      }
    });

    showSnackbar('Lead saved!', 'success');
    hardReset();
    setConfirmOpen(false);

    // If user clicked "Create Lead" while already here, we still proceed (same route) — will just keep the reset state.
    pendingNav.proceed();
    setPendingNav(null);
  } catch (err) {
    showSnackbar(String(err), 'error');
  } finally {
    setSaving(false);
  }
};

const resetThenProceed = () => {
  if (!pendingNav) return;
  hardReset();
  setConfirmOpen(false);
  pendingNav.proceed();
  setPendingNav(null);
};

const stayHere = () => {
  setConfirmOpen(false);
  setPendingNav(null);
};


  useEffect(() => {
    if (state?.reset) {
      hardReset();
      // Optional: strip the reset token so back/forward doesn't retrigger
      navigate('.', { replace: true, state: {} });
    }
  }, [state?.reset, hardReset, navigate]);
    const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.(com|in|org)$/i.test(email);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleEditNote = (note) => {
    setSelectedNote(note);
    setEditableNote({ type: note.type, message: note.message });
    setEditNoteDialog(true);
  };
   const handleDeleteClick = (noteId) => {
    setNoteToDelete(noteId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (noteToDelete) {
      try {
        await dispatch(getDeleteNoteAction(noteToDelete));
         dispatch(getLeadsByIdAction(leadData.id));
        setDeleteConfirmOpen(false);
        setNoteToDelete(null);
        // Optional: Show success notification
      } catch (error) {
        // Optional: Show error notification
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setNoteToDelete(null);
  };
  const handleCloseEditDialog = () => {
    setEditNoteDialog(false);
    setSelectedNote(null);
    setEditableNote({ type: '', message: '' });
    setNoteDialogErrors({ type: '', message: '' });
  };


  // Is anything filled at all?
const isDirty = React.useMemo(() => {
  // quick structural check
  const base = { ...INITIAL_FORM, followUpDate: null, notes: [] };
  const now  = { ...formData, followUpDate: formData.followUpDate ?? null, notes: formData.notes ?? [] };

  const shallowChanged =
    now.firstName || now.middleName || now.lastName ||
    now.email || now.rawPhoneNumber || now.quoteNumber ||
    now.status || now.type || now.lineOfBusiness || now.leadSource ||
    now.assignedTo || now.followUpDate || (Array.isArray(now.notes) && now.notes.length);

  return !!shallowChanged;
}, [formData]);


   const handleSaveNote = () => {
    // Validate fields
    let tempErrors = { type: '', message: '' };
    let hasError = false;

    if (!editableNote.type || !editableNote.type.trim()) {
      tempErrors.type = 'Note type is required';
      hasError = true;
    }

    if (!editableNote.message || !editableNote.message.trim()) {
      tempErrors.message = 'Note message is required';
      hasError = true;
    }

    setNoteDialogErrors(tempErrors);

    // If there are errors, don't proceed
    if (hasError) {
      showSnackbar('Please fill both the fields', 'error');
      return;
    }

    // If validation passes, proceed with saving
    const updatedNotes = leadContent.map(note =>
      note.id === selectedNote.id
        ? { ...note, type: editableNote.type, message: editableNote.message }
        : note
    );

    const payload = {
      id: leadData.id,
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      phoneNumber: convertToE164(formData.rawPhoneNumber),
      email: formData.email,
      type: formData.type,
      leadSource: formData.leadSource,
      status: formData.status,
      quoteNumber: formData.quoteNumber,
      lineOfBusiness: formData.lineOfBusiness,
      leadMetaInformation: leadData.leadMetaInformation || "",
      assignedTo: formData.assignedTo,
      followUpDate: formData.followUpDate,
      username: leadData.username || "",
      doNotEmail: leadData.doNotEmail || false,
      sendLoginInfoByEmail: leadData.sendLoginInfoByEmail || false,
      notes: updatedNotes.map(note => ({
        id: note.id,
        message: note.message,
        type: note.type
      }))
    };

    dispatch(updateLead(payload));
    handleCloseEditDialog();
    dispatch(getLeadsByIdAction(leadData.id));
  };

  const handleNoteFieldChange = (field, value) => {
    setEditableNote(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear the error for this field when user starts typing
  if (editNoteDialog) {
    setNoteDialogErrors(prev => ({
      ...prev,
      [field]: ''
    }));
  }
  };

  const handleUserInputChange = (event, newInputValue) => {
    if (userSearchTimeout) clearTimeout(userSearchTimeout);
    const timeoutId = setTimeout(() => {
      if (newInputValue?.length >= 3) {
        dispatch(searchUserOnTaskAction(newInputValue));
      }
    }, 300);
    setUserSearchTimeout(timeoutId);
  };

  useEffect(() => {
    if (leadData?.id) {
      dispatch(getLeadsByIdAction(leadData.id));
    }
  }, [dispatch, leadData]);

  const leadById = useSelector((state) => state.leads.leadsById);
  const leadContent = leadById?.content;

  useEffect(() => {
    if (leadData) {
      setFormData({
        firstName: leadData.firstName || '',
        middleName: leadData.middleName || '',
        lastName: leadData.lastName || '',
        phoneNumber: formatPhoneNumber(leadData.phoneNumber || ''),
        rawPhoneNumber: leadData.phoneNumber?.replace(/\D/g, '').slice(-10) || '',
        email: leadData.email || '',
        quoteNumber: leadData.quoteNumber || '',
        status: leadData.status || '',
        assignedTo: leadData.assignedTo || '',
        assignedToName: leadData.assignedToName || '',
        type: leadData.type || '',
        lineOfBusiness: leadData.lineOfBusiness || '',
        leadSource: leadData.leadSource || '',
        followUpDate : leadData.followUpDate  || null,
      });
    }
  }, [leadData, leadById]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "phoneNumber") {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      newValue = formatPhoneNumber(digits);
      setFormData({
        ...formData,
        phoneNumber: newValue,
        rawPhoneNumber: digits,
      });
      setErrors((prev) => ({ ...prev, phoneNumber: undefined }));
    } else if (["firstName", "middleName", "lastName"].includes(name)) {
      newValue = value.replace(/[^A-Za-z\s]/g, "").slice(0, 50);
      setFormData({ ...formData, [name]: newValue });
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    } else if (name === "email") {
      newValue = value.slice(0, 50);
      setFormData({ ...formData, email: newValue });

      if (newValue.length > 0 && !validateEmail(newValue)) {
        setErrors((prev) => ({
          ...prev,
          email: "Invalid email format.",
        }));
      } else {
        setErrors((prev) => ({ ...prev, email: undefined }));
      }
    } else if (name === "quoteNumber") {
      const alphanumericRegex = /^[a-zA-Z0-9]*$/;
      const trimmedValue = value.slice(0, 15);

      setFormData({ ...formData, quoteNumber: trimmedValue });

      if (!trimmedValue) {
        setErrors((prev) => ({ ...prev, quoteNumber: "Quote number is required" }));
      } else if (!alphanumericRegex.test(trimmedValue)) {
        setErrors((prev) => ({ ...prev, quoteNumber: "Only basic alphanumeric characters" }));
      } else {
        setErrors((prev) => ({ ...prev, quoteNumber: undefined }));
      }
    } else {
      setFormData({ ...formData, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
};


  const handleNoteChange = (index, field, value) => {
  const updatedNotes = [...formData.notes];
  updatedNotes[index] = {
    ...updatedNotes[index],
    [field]: value,
  };
  setFormData({ ...formData, notes: updatedNotes });

  // Clear error for this specific field when user starts typing
  if (value && value.trim()) {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`note_${field}_${index}`];
      return newErrors;
    });
  }
};

  const addNote = () => {
    setFormData({
      ...formData,
      notes: Array.isArray(formData.notes) ? [...formData.notes, { message: '', type: '' }] : [{ message: '', type: '' }],
    });
  };

  const removeNote = (index) => {
    const updatedNotes = formData.notes.filter((_, i) => i !== index);
    setFormData({ ...formData, notes: updatedNotes });
  };

  const validate = () => {
    let tempErrors = {};
    const nameRegex = /^[A-Za-z\s]+$/;

    if (!formData.firstName) {
      tempErrors.firstName = 'First name is required';
    } else if (formData.firstName.length > 50) {
      tempErrors.firstName = 'First name cannot exceed 50 characters';
    } else if (!nameRegex.test(formData.firstName)) {
      tempErrors.firstName = 'First name can only contain letters and spaces';
    }

    if (formData.middleName) {
      if (formData.middleName.length > 50) {
        tempErrors.middleName = 'Middle name cannot exceed 50 characters';
      } else if (!nameRegex.test(formData.middleName)) {
        tempErrors.middleName = 'Middle name can only contain letters and spaces';
      }
    }

    if (!formData.lastName) {
      tempErrors.lastName = 'Last name is required';
    } else if (formData.lastName.length > 50) {
      tempErrors.lastName = 'Last name cannot exceed 50 characters';
    } else if (!nameRegex.test(formData.lastName)) {
      tempErrors.lastName = 'Last name can only contain letters and spaces';
    }


    if (!formData.email) {
      tempErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      tempErrors.email = 'Invalid email format';
    }

    if (!formData.assignedTo) tempErrors.assignedTo = 'Assigned To is required';
    if (!formData.leadSource) tempErrors.leadSource = 'Lead Source is required';
    if (!formData.status) tempErrors.status = 'Status is required';
    if (!formData.type) tempErrors.type = 'Type of Lead is required';
    if (!formData.lineOfBusiness) tempErrors.lineOfBusiness = 'Business Type is required';
    if (formData.rawPhoneNumber.length !== 10) {
      tempErrors.phoneNumber = 'Phone number must be exactly 10 digits';
    }
    if (!formData.quoteNumber) tempErrors.quoteNumber = 'Quote number is required';

    return tempErrors;
  };

  const handleDateChange = (fieldName, formattedDate) => {
    setFormData((prev) => ({ ...prev, [fieldName]: formattedDate }));
  };

  const handleBlur = (fieldName, errorMessage) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    setErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
};


  const handleSubmit = () => {
  const validationErrors = validate();
  let noteErrors = {};
  let hasNoteErrors = false;

  // Validate notes if they exist
  if (formData.notes && formData.notes.length > 0) {
    formData.notes.forEach((note, index) => {
      // Check if at least one field is filled
      const hasType = note.type && note.type.trim() !== '';
      const hasMessage = note.message && note.message.trim() !== '';

      // If at least one field has content, both must be filled
      if (hasType || hasMessage) {
        if (!hasType) {
          noteErrors[`note_type_${index}`] = 'Note type is required';
          hasNoteErrors = true;
        }
        if (!hasMessage) {
          noteErrors[`note_message_${index}`] = 'Note message is required';
          hasNoteErrors = true;
        }
      }
      // If both fields are empty, we'll just filter it out (no error needed)
    });
  }

  // Combine all errors
  const allErrors = { ...validationErrors, ...noteErrors };
  setErrors(allErrors);

  // Check if there are any errors
  if (Object.keys(validationErrors).length === 0 && !hasNoteErrors) {
    let allNotes = [];

    if (isEditMode) {
      // Include existing notes
      if (leadContent && leadContent.length > 0) {
        allNotes = leadContent.map(note => ({
          id: note.id,
          message: note.message,
          type: note.type
        }));
      }

      // Add new notes (only complete ones)
      if (formData.notes && formData.notes.length > 0) {
        const newNotes = formData.notes
          .filter(note =>
            note.message && note.message.trim() !== '' &&
            note.type && note.type.trim() !== ''
          )
          .map(note => ({
            message: note.message,
            type: note.type
          }));
        allNotes = [...allNotes, ...newNotes];
      }
    } else {
      // Create mode - only include complete notes
      if (formData.notes && formData.notes.length > 0) {
        allNotes = formData.notes.filter(note =>
          note.message && note.message.trim() !== '' &&
          note.type && note.type.trim() !== ''
        );
      }
    }

    const payload = {
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      phoneNumber: convertToE164(formData.rawPhoneNumber),
      email: formData.email,
      type: formData.type,
      leadSource: formData.leadSource,
      status: formData.status,
      quoteNumber: formData.quoteNumber,
      lineOfBusiness: formData.lineOfBusiness,
      notes: allNotes,
      assignedTo: formData.assignedTo,
      followUpDate: formData.followUpDate,
      ...(isEditMode && { id: leadData.id }),
    };

    const onSuccess = () => {
      showSnackbar(isEditMode ? 'Lead updated successfully!' : 'Lead created successfully!');
      dispatch({ type: "RESET_ALL_LEADS" });
      setTimeout(() => {
        navigate('/allleads');
      }, 1500);
    };

    const onError = (error) => {
      showSnackbar(error || `Failed to ${isEditMode ? 'update' : 'create'} lead. Please try again.`, 'error');
    };

    if (isEditMode) {
      dispatch(updateLead(payload, onSuccess, onError));
    } else {
      dispatch(createLeadAction(payload, onSuccess, onError));
    }
  } else {
    // More specific error message
    if (hasNoteErrors) {
      showSnackbar('Please fill both type and message for all notes or remove empty notes', 'error');
    } else {
      showSnackbar('Please fix the errors in the form', 'error');
    }
  }
};

  const noteTypeOptions = [
    'Billing',
    'Cancellation',
    'Claims',
    'Cross-Sell Follow Up',
    'Customer Concerns/Questions',
    'Follow Up',
    'Pending Cancellation',
    'Policy Changes',
    'Policy Review',
    'Quote',
    'Renewal',
    'Underwriting',
    'General',
  ];

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formFields = [
    {
      key: 'firstName',
      label: 'First Name',
      required: true,
      gridSize: { xs: 12, sm: 6, md: 4, lg: 2 }
    },
    {
      key: 'middleName',
      label: 'Middle Name',
      required: false,
      gridSize: { xs: 12, sm: 6, md: 4, lg: 2 }
    },
    {
      key: 'lastName',
      label: 'Last Name',
      required: true,
      gridSize: { xs: 12, sm: 6, md: 4, lg: 2 }
    },
    {
      key: 'phoneNumber',

      gridSize: { xs: 12, sm: 6, md: 4, lg: 2 }
    },
    {
      key: 'email',
      label: 'Email',
      required: true,
      gridSize: { xs: 12, sm: 6, md: 4, lg: 2 }
    },
    {
      key: 'quoteNumber',
      label: 'Quote Number',
      required: true,
      gridSize: { xs: 12, sm: 6, md: 4, lg: 2 }
    },
  ];

  const selectFields = [
    {
      key: 'status',
      label: 'Status',
      options: ['New', 'Contacted', 'Contact Back', 'Potential Sale', 'Closed - Won', 'Closed - Lost'],
      gridSize: { xs: 12, sm: 6, md: 4, lg: 2 }
    },
    {
      key: 'type',
      label: 'Type of Lead',
      options: [
        { value: 'Personal', label: 'Personal' },
        { value: 'Commercial', label: 'Commercial' }
      ],
      gridSize: { xs: 12, sm: 6, md: 4, lg: 2 }
    },
    {
      key: 'lineOfBusiness',
      label: 'Line of Business',
      options: [
        { value: 'Personal Auto', label: 'Personal Auto' },
        { value: 'Commercial Auto', label: 'Commercial Auto' },
        { value: 'Homeowners', label: 'Homeowners' },
        { value: 'Health', label: 'Health' },
        { value: 'General Liability', label: 'General Liability' }
      ],
      gridSize: { xs: 12, sm: 6, md: 4, lg: 2 }
    },
    {
      key: 'leadSource',
      label: 'Source of Lead',
      options: [
        { value: 'Referral', label: 'Referral' },
        { value: 'Website', label: 'Website' },
        { value: 'Walk-in', label: 'Walk-in' },
        { value: 'Advertisement', label: 'Advertisement' }
      ],
      gridSize: { xs: 12, sm: 6, md: 4, lg: 2 }
    }
  ];


// Is the form fully valid (your existing validate())
const isFormValid = React.useCallback(() => {
  return Object.keys(validate()).length === 0;
}, [validate]);


 useEffect(() => {
  const onNavIntent = (e) => {
    const { path, state, cancel, proceed } = e.detail || {};

    if (isDirty && isFormValid()) {
      cancel(); // stop immediate navigation
      setPendingNav({ path, state, proceed });
      setConfirmOpen(true);
    }
  };

    window.addEventListener('app:navigation-intent', onNavIntent);
    return () => window.removeEventListener('app:navigation-intent', onNavIntent);
  }, [isDirty, isFormValid]);
  return (
    <Container
      maxWidth={false}
      sx={{
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 2, sm: 3 },
        maxWidth: '100%'
      }}
    >
      {/* Header */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          sx={{
            fontWeight: 600,
            color: '#333',
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          {isEditMode ? 'Edit Lead' : 'Create Lead'}
        </Typography>
      </Box>

      {/* Main Form */}
      <Card sx={{
        boxShadow: { xs: 1, sm: 3 },
        borderRadius: { xs: 1, sm: 2 }
      }}>
        <CardContent sx={{
          p: { xs: 2, sm: 3, md: 4 }
        }}>
          <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>

            {/* Basic Information Fields */}
            {formFields.map(({ key, label, required, gridSize }) => (
              <Grid item {...gridSize} key={key}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {label} {required && <span style={{ color: 'red' }}>*</span>}
                </Typography>
               {key === "phoneNumber" ? (
                  <PhoneNumberField
                    name="phoneNumber"
                    value={formData.rawPhoneNumber || ""}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        phoneNumber: raw,
                        rawPhoneNumber: raw,
                      }));
                      setErrors((prev) => ({ ...prev, phoneNumber: undefined }));
                    }}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                    size={isMobile ? "small" : "medium"}
                    labelStyle={{ fontSize: "16px", fontWeight: 500 ,mb:1,mt:-1 }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    error={!!errors[key]}
                    helperText={errors[key]}
                    inputProps={key === "quoteNumber" ? { maxLength: 15 } : {}}
                  />
                )}
              </Grid>
            ))}

            {/* Select Fields */}
            {selectFields.map(({ key, label, options, gridSize }) => (
              <Grid item {...gridSize} key={key}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {label} <span style={{ color: 'red' }}>*</span>
                </Typography>
                  <FormControl fullWidth error={!!errors[key]}>
                    <Select
                      native
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      size={isMobile ? "small" : "medium"}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      <option value="" disabled>Select {label}</option>
                      {options.map((opt) => {
                        const value = typeof opt === 'string' ? opt : opt.value;
                        const label = typeof opt === 'string' ? opt : opt.label;
                        return (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        );
                      })}
                    </Select>
                    {errors[key] && (
                      <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                        {errors[key]}
                      </Typography>
                    )}
                  </FormControl>

              </Grid>
            ))}

            {/* Assigned To Field */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                Assigned To <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Autocomplete
                options={searchUsers}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : `${option.firstName || ''} ${option.lastName || ''}`
                }
                value={formData.assignedToName || null}
                onInputChange={handleUserInputChange}
                onChange={(event, newValue) => {
                  const assignedId = newValue?.id || '';
                  const assignedName = newValue ? `${newValue.firstName || ''} ${newValue.lastName || ''}` : '';
                  setFormData((prev) => ({ ...prev, assignedTo: assignedId, assignedToName: assignedName }));
                  setErrors((prev) => ({ ...prev, assignedTo: newValue ? undefined : 'Assigned To is required' }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search users..."
                    error={!!errors.assignedTo}
                    helperText={errors.assignedTo}
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }
                    }}
                  />
                )}
              />
            </Grid>


 {/* Follow-up Date */}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
              Follow-up Date
            </Typography>
            <DatePickerField
              fieldName="followUpDate"
              formData={formData}
              handleDateChange={handleDateChange}
              handleBlur={handleBlur}
              touchedFields={{ followUpDate: true }}
              fieldErrors={errors}
              size={isMobile ? "small" : "medium"}
            />
          </Grid>

          </Grid>

          <Divider sx={{ my: { xs: 3, sm: 4 } }} />

         {/* Notes Section */}
<Box>
  <Typography
    variant={isMobile ? "h6" : "h5"}
    sx={{
      mb: { xs: 2, sm: 3 },
      fontWeight: 600,
      fontSize: { xs: '1.125rem', sm: '1.25rem' }
    }}
  >
    Notes
  </Typography>

  {/* Add Notes for Create Mode OR New Notes in Edit Mode */}
{Array.isArray(formData.notes) && formData.notes.length > 0 && (
  <Box sx={{ mb: 2 }}>
    <Typography className="btn-primary" variant="h6" sx={{
      mb: 2,
      height: '10px',
      padding: '4px 14px',
      display: 'flex',
      alignItems: 'center'
    }}>
      {isEditMode ? 'New Notes' : 'Notes'}
    </Typography>
    {formData.notes.map((note, index) => (
      <Card
        key={index}
        variant="outlined"
        sx={{
          mb: 2,
          borderColor: errors[`note_message_${index}`] || errors[`note_type_${index}`] ? 'error.main' : 'grey.300'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={4} md={3}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                Note Type
                {/* Show asterisk if note has some content */}
                {(note.message || note.type) && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              <FormControl fullWidth error={!!errors[`note_type_${index}`]}>
                <Select
                  native
                  value={note.type}
                  onChange={(e) => handleNoteChange(index, 'type', e.target.value)}
                  size={isMobile ? "small" : "medium"}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  <option value="">Select Note Type</option>
                  {noteTypeOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Select>
                {errors[`note_type_${index}`] && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    {errors[`note_type_${index}`]}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={7} md={8}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                Message
                {/* Show asterisk if note has some content */}
                {(note.message || note.type) && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 3 : 2}
                value={note.message || ''}
                onChange={(e) => handleNoteChange(index, 'message', e.target.value)}
                error={!!errors[`note_message_${index}`]}
                helperText={errors[`note_message_${index}`]}
                placeholder="Enter note message..."
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={1} md={1}>
              <Box sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-end', sm: 'center' },
                alignItems: 'flex-start',
                pt: { xs: 0, sm: 4 }
              }}>
                <IconButton
                  onClick={() => removeNote(index)}
                  color="error"
                  size={isMobile ? "small" : "medium"}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    ))}
  </Box>
)}
  {/* Display existing notes for Edit Mode */}
  {isEditMode && leadContent && leadContent.length > 0 && (
    <Box sx={{ mb: 3 }}>
      <Box className ='btn-primary' sx={{
    mb: 2,
    display: "flex",
    alignItems: "center",
    height: 48,
  }}>
      <Typography variant="h6" sx={{ ml:2,mt:0 }}>
      Existing Notes
      </Typography>
      </Box>

      {isMobile ? (
        <Box>
          {leadContent
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((note) => (
              <Card key={note.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Note ID
                      </Typography>
                      <Typography variant="body2">{note.pubId}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Type
                      </Typography>
                      <Typography variant="body2">{note.type}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">
                        Message
                      </Typography>
                      <Typography variant="body2">{note.message}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Source
                      </Typography>
                      <Typography variant="body2">{note.notesSource}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Created By
                      </Typography>
                      <Typography variant="body2">{note.createdBy?.name}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">
                        Created On
                      </Typography>
                      <Typography variant="body2">
                        {note.createdBy?.timestamp
                          ? new Date(note.createdBy.timestamp).toLocaleString()
                          : '—'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
        </Box>
      ) : (

        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell className="styled-table-heading">Note ID</TableCell>
              <TableCell className="styled-table-heading">Type</TableCell>
              <TableCell className="styled-table-heading">Message</TableCell>
              <TableCell className="styled-table-heading">Source</TableCell>
              <TableCell className="styled-table-heading">Created By</TableCell>
              <TableCell className="styled-table-heading">Created On</TableCell>
              <TableCell className="styled-table-heading">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leadContent
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((note) => (
                <TableRow className="styled-table-row" key={note.id}>
                  <TableCell>{note.pubId}</TableCell>
                  <TableCell>{note.type}</TableCell>
                  <TableCell>{note.message}</TableCell>
                  <TableCell>{note.notesSource}</TableCell>
                  <TableCell>{note.createdBy?.name}</TableCell>
                  {/* <TableCell>
                    {note.createdBy?.timestamp
                      ? new Date(note.createdBy.timestamp).toLocaleString()
                      : '—'}
                  </TableCell> */}
                  <CustomTableCell timestamp={note.createdBy?.timestamp} />
                  <TableCell>
                    <IconButton
                      onClick={() => handleEditNote(note)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                     <IconButton
                                              onClick={() => handleDeleteClick(note.id)}
                                              size="small"
                                              sx={{
                                                color: 'black',
                                                '&:hover': { bgcolor: 'grey.200' }
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      )}
      {/* Delete note */}
       <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>Delete Note</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this note? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Note Dialog - keep your existing dialog code */}
      <Dialog
        open={editNoteDialog}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        {/* ... your existing dialog code ... */}
         <DialogTitle>Edit Note</DialogTitle>
  <DialogContent>
    {selectedNote && (
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500,fontSize:20 }}>
            Note Type
          </Typography>
          <FormControl fullWidth    sx={{ mb: 2 }}
          error={!!noteDialogErrors.type}>
            <Select
              value={editableNote.type}
              onChange={(e) => handleNoteFieldChange('type', e.target.value)}
              size="medium"
            >
              {noteTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500,fontSize:20 }}>
            Message
            {/* <span style={{ color: 'red' }}>*</span> */}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={editableNote.message}
            onChange={(e) => handleNoteFieldChange('message', e.target.value)}
            placeholder="Enter note message..."
            variant="outlined"
             error={!!noteDialogErrors.message}
          helperText={noteDialogErrors.message}
          />
        </Grid>
      </Grid>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={handleCloseEditDialog} color="secondary">
      Cancel
    </Button>
    <Button
     className="btn-primary"
      onClick={handleSaveNote}
      color="primary"
      variant="contained"
      disabled={!editableNote.type || !editableNote.message.trim()}
      sx={{
  background: (!editableNote.type || !editableNote.message.trim())
    ? "rgba(128, 128, 128, 0.6)" // Gray background when disabled
    : "rgba(7, 123, 253, 0.6)",

  "&:hover": {
    background: (!editableNote.type || !editableNote.message.trim())
      ? "rgba(128, 128, 128, 0.6)" // Keep gray on hover when disabled
      : "rgba(7, 123, 253, 1)",
  },

  "&:disabled": {
    color: "#fff",
    opacity: 0.6,
  },
}}
    >
      Update
    </Button>
  </DialogActions>
      </Dialog>

      {/* Pagination - keep your existing pagination code */}
      {leadContent.length > 0 && (
        <Box sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-end" },
          alignItems: "center",
          padding: "1rem",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 1 }
        }}>
          {/* ... your existing pagination code ... */}
           <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: { xs: "12px", sm: "14px" }
                      }}>
                        <span style={{ marginRight: 8 }}>Rows per page:</span>
                        <select
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                          style={{
                            padding: "6px 12px",
                            borderRadius: "4px",
                            borderColor: "#ccc",
                            fontSize: isMobile ? "12px" : "14px"
                          }}
                        >
                          {[5, 10, 15, 25].map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </Box>
                    <Pagination
                        count={Math.ceil(leadContent.length / rowsPerPage)}
                        page={page + 1}
                        onChange={(event, value) => handlePageChange(event, value - 1)}
                        showFirstButton
                        showLastButton
                        size={isMobile ? "small" : "medium"}
                        sx={{ ml: { xs: 0, sm: 2 } }}
                      />
                  </Box>
                )}
              </Box>
            )}

            {/* Show message when no existing notes in edit mode */}
            {isEditMode && (!leadContent || leadContent.length === 0) && (!formData.notes || formData.notes.length === 0) && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                No notes available
              </Typography>
            )}
            <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2
            }}
          >
            {/* Add Note Button - Now visible in both create and edit modes */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addNote}
              fullWidth={isMobile}
              sx={{
                mt: 2,
                textTransform: 'none',
                borderRadius: 1,
                py: { xs: 1.5, sm: 1 },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Add Note
            </Button>
            </Box>
            {errors.notes && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                {errors.notes}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{
        mt: { xs: 3, sm: 4 },
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 2 },
        justifyContent: { xs: 'stretch', sm: 'flex-end' }
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/allleads')}
          fullWidth={isMobile}
          sx={{
            textTransform: 'none',
            py: { xs: 1.5, sm: 1 },
            fontSize: { xs: '0.875rem', sm: '1rem' },
            order: { xs: 2, sm: 1 }, 
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          fullWidth={isMobile}
          sx={{
            textTransform: 'none',
            py: { xs: 1.5, sm: 1 },
            fontSize: { xs: '0.875rem', sm: '1rem' },
            order: { xs: 1, sm: 2 }, '&:focus': {
                },
          }}
        >
          {isEditMode ? 'Update' : 'Save'}
        </Button>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={confirmOpen} onClose={stayHere} maxWidth="sm" fullWidth>
        <DialogTitle className="btn-primary" sx={{
          height: 40, display: 'flex', alignItems: 'center', px: 3
        }}>
          Leave this page?
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Typography sx={{ mb: 1 }}>
            You’ve filled the Create Lead form. What would you like to do?
          </Typography>
          <ul style={{ marginTop: 8, paddingLeft: 18 }}>
            <li><strong>Save & Reset</strong>: saves the lead, clears the form, then continues to your selection.</li>
            <li><strong>Reset without saving</strong>: clears the form and continues without saving.</li>
            <li><strong>Stay here</strong>: keep editing.</li>
          </ul>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={stayHere} color="secondary">Stay here</Button>
          <Button onClick={resetThenProceed} variant="outlined" className="btn-secondary">
            Reset without saving
          </Button>
          <Button
            onClick={saveAndResetThenProceed}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save & Reset'}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>


  );
};

export default CreateLeadForm;